
import React, { useState, useMemo } from 'react';
import type { ScheduleEntry, Shift, AssignmentStatus, DayOfWeek, AvailabilityStatus, Department, AttendanceRecord, AttendanceRequest } from '../../types';
import { ALL_ASSIGNMENT_STATUSES } from '../../types';
import Popover from '../ui/Popover';
import { Icons } from '../ui/Icons';
import { toast } from 'react-hot-toast';
import { getDayOfWeekFromDate, formatDateYYYYMMDD } from '../../utils/dateUtils';
import ConfirmationModal from '../ui/ConfirmationModal';
import Modal from '../ui/Modal';


interface ScheduleCellProps {
    staffId: string;
    date: string;
    entry?: ScheduleEntry;
    shifts: Shift[];
    onUpdate?: (staffId: string, date: string, shiftId: string | null, status: AssignmentStatus, isForced?: boolean, departmentId?: string | null, customUpdates?: Partial<ScheduleEntry>) => void;
    draggedShiftId?: string | null;
    personName: string;
    personAvailability?: {
        availability: { [key in DayOfWeek]: AvailabilityStatus };
        availabilityOverrides?: { [date: string]: AvailabilityStatus };
    };
    staffDepartments?: Department[];
    allDepartments?: Department[];
    readOnly?: boolean;
    isAdmin?: boolean;
    isSuperAdmin?: boolean;
    attendanceRecords?: AttendanceRecord[];
    attendanceRequests?: AttendanceRequest[];
    onUpdateAttendance?: (staffId: string, date: string, updates: { actualStartTime: string, actualEndTime: string, staffNote?: string, adminNote?: string }) => void;
    onCreateAttendanceRequest?: (staffId: string, date: string, updates: { requestedStartTime: string, requestedEndTime: string, staffNote?: string }) => void;
    onViewDetails?: (staffId: string) => void;
}

const statusConfig: { [key in AssignmentStatus]: { label: string, icon: React.ReactNode, color: string } } = {
    ASSIGNED: { label: 'Assigned', icon: <Icons.Clock className="w-5 h-5" />, color: 'bg-gray-200 text-gray-800 dark:bg-slate-600 dark:text-slate-100' },
    OFF: { label: 'Off', icon: <Icons.Coffee className="w-5 h-5" />, color: 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300' },
    SICK: { label: 'Sick', icon: <Icons.Thermometer className="w-5 h-5" />, color: 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400' },
    VACATION: { label: 'Vacation', icon: <Icons.Plane className="w-5 h-5" />, color: 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400' },
    ON_CALL: { label: 'On Call', icon: <Icons.Bell className="w-5 h-5" />, color: 'bg-orange-50 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400' },
    REFUSED: { label: 'Refused', icon: <Icons.XCircle className="w-5 h-5" />, color: 'bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400' },
    UNASSIGNED: { label: '(off)', icon: <Icons.HelpCircle className="w-5 h-5" />, color: 'bg-gray-100 text-gray-500 dark:bg-slate-700/50 dark:text-slate-400' },
};

const formatTimeToAMPM = (time: string): string => {
    if (!time) return '';
    const [hoursStr, minutesStr] = time.split(':');
    let hours = parseInt(hoursStr, 10);
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    return `${hours}${ampm}`;
};


const ScheduleCell: React.FC<ScheduleCellProps> = ({ staffId, date, entry, shifts, onUpdate, draggedShiftId, personName, personAvailability, staffDepartments = [], allDepartments = [], readOnly, isAdmin, isSuperAdmin, attendanceRecords = [], attendanceRequests = [], onUpdateAttendance, onCreateAttendanceRequest, onViewDetails }) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const [forceAssignData, setForceAssignData] = useState<{ shiftId: string | null, status: AssignmentStatus, departmentId?: string | null } | null>(null);
    const [pendingShiftId, setPendingShiftId] = useState<string | null>(null);
    const [isTimeUpdateModalOpen, setIsTimeUpdateModalOpen] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

    // Snapshot priority logic
    const shiftInfo = useMemo(() => {
        if (!entry || entry.status !== 'ASSIGNED') return null;
        
        // Find shift template if snapshots are missing (legacy data)
        const shiftTemplate = entry.shiftId ? shifts.find(s => s.id === entry.shiftId) : null;
        
        return {
            name: entry.shiftNameSnapshot || shiftTemplate?.name || 'Assigned',
            color: entry.shiftColorSnapshot || shiftTemplate?.color || '#cbd5e1', // default gray if no color
            startTime: entry.assignedStartTime || shiftTemplate?.startTime || '',
            endTime: entry.assignedEndTime || shiftTemplate?.endTime || ''
        };
    }, [entry, shifts]);

    const activeRequest = useMemo(() => {
        // Find the latest Request for this specific date/employee
        const requests = attendanceRequests.filter(r => r.employeeId === staffId && r.date === date);
        if (requests.length === 0) return null;
        // Sort by createdAt descending
        return requests.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
    }, [attendanceRequests, staffId, date]);

    const hasPendingAttendanceRequest = activeRequest?.status === 'PENDING';

    const isShiftPastOrOngoing = useMemo(() => {
        if (!shiftInfo) return false;
        
        const [year, month, day] = date.split('-').map(Number);
        const [startHours, startMinutes] = shiftInfo.startTime.split(':').map(Number);
        
        let shiftStart = new Date(year, month - 1, day, startHours, startMinutes);
        
        // BUFFER: Allow editing 1 hour before shift starts
        shiftStart.setHours(shiftStart.getHours() - 1);
        
        return new Date() >= shiftStart;
    }, [date, shiftInfo]);
    
    // Use allDepartments to resolve the assigned department for display
    const assignedDepartment = useMemo(() => {
        if (!entry || entry.status !== 'ASSIGNED') return null;
        
        // 1. Try to find by the specific departmentId in the entry
        if (entry.departmentId) {
            const found = allDepartments.find(d => d.id === entry.departmentId);
            if (found) return found;
        }
        
        // 2. Fallback: Try to find by the staff member's first department
        if (staffDepartments && staffDepartments.length > 0) {
            return staffDepartments[0];
        }
        
        return null;
    }, [entry, allDepartments, staffDepartments]);

    const { isSpecificUnavailability, isDefaultUnavailability } = useMemo(() => {
        if (!personAvailability) {
            return { isSpecificUnavailability: false, isDefaultUnavailability: false };
        }
        
        // Safe date parsing to local time to avoid timezone issues with YYYY-MM-DD strings
        const [year, month, day] = date.split('-').map(Number);
        const localDate = new Date(year, month - 1, day);
        const dayOfWeek = getDayOfWeekFromDate(localDate);

        const override = personAvailability.availabilityOverrides?.[date];
        const resolvedStatus = override || (personAvailability.availability && personAvailability.availability[dayOfWeek]) || 'Available';
        
        return {
            isSpecificUnavailability: resolvedStatus === 'Unavailable' && !!override,
            isDefaultUnavailability: resolvedStatus === 'Unavailable' && !override,
        };
    }, [date, personAvailability]);

    const tooltipText = useMemo(() => {
        if (isSpecificUnavailability) return `${personName} has marked this date as specifically unavailable.`;
        if (entry?.status === 'ASSIGNED') {
            const deptName = assignedDepartment?.name || 'Assigned';
            return `Department: ${deptName}`;
        }
        if (entry?.status && entry.status !== 'UNASSIGNED') {
            const label = statusConfig[entry.status]?.label || entry.status;
            return `Status: ${label}`;
        }
        return undefined;
    }, [isSpecificUnavailability, entry?.status, assignedDepartment, personName]);

    const handleAssignmentAttempt = (shiftId: string | null, status: AssignmentStatus, departmentId?: string | null) => {
        if (readOnly || !onUpdate) return;
        // Allow setting non-assignment statuses (like SICK, VACATION) regardless of availability
        if (status !== 'ASSIGNED') {
            onUpdate(staffId, date, shiftId, status, false, departmentId);
            return;
        }

        // Hard lock for specific unavailability
        if (isSpecificUnavailability) {
            toast.error('This staff member is not available on this specific date.');
            return;
        }

        // If multiple departments, and no departmentId provided, we need to ask
        if (staffDepartments.length > 1 && !departmentId) {
            setPendingShiftId(shiftId);
            return;
        }

        // Use primary department if none provided and only one exists
        const finalDeptId = departmentId || (staffDepartments.length === 1 ? staffDepartments[0].id : null);

        // Soft lock for default unavailability
        if (isDefaultUnavailability) {
            setForceAssignData({ shiftId, status, departmentId: finalDeptId });
            return;
        }
        
        onUpdate(staffId, date, shiftId, status, false, finalDeptId);
    };

    const handleConfirmForceAssign = () => {
        if (forceAssignData && onUpdate) {
            onUpdate(staffId, date, forceAssignData.shiftId, forceAssignData.status, true, forceAssignData.departmentId);
        }
        setForceAssignData(null);
    };

    const handleCancelForceAssign = () => {
        setForceAssignData(null);
    };

    const handleDrop = (e: React.DragEvent<HTMLTableCellElement>) => {
        if (readOnly) return;
        e.preventDefault();
        setIsDragOver(false);
        if (isSpecificUnavailability) return;
        
        const droppedShiftId = e.dataTransfer.getData('shiftId');
        if (droppedShiftId) {
            handleAssignmentAttempt(droppedShiftId, 'ASSIGNED');
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLTableCellElement>) => {
        if (readOnly) return;
        e.preventDefault();
        if (!isSpecificUnavailability) {
            setIsDragOver(true);
        }
    };

    const handleDragLeave = () => {
        if (readOnly) return;
        setIsDragOver(false);
    };
    
    const PopoverContent = ({ close }: { close: () => void }) => {
        const availableShifts = shifts.filter(s => s.name.toLowerCase() !== 'off');
        const statusLeaveOptions = ALL_ASSIGNMENT_STATUSES.filter(s => s !== 'ASSIGNED' && s !== 'OFF' && s !== 'UNASSIGNED');
        
        const [customStart, setCustomStart] = useState(entry?.customStartTime || shiftInfo?.startTime || '');
        const [customEnd, setCustomEnd] = useState(entry?.customEndTime || shiftInfo?.endTime || '');

        const handleApplyCustom = () => {
            if (shiftInfo && entry?.shiftId) {
                onUpdate(staffId, date, entry.shiftId, 'ASSIGNED', false, entry?.departmentId, {
                    customStartTime: customStart,
                    customEndTime: customEnd,
                    isTimeEdited: true
                });
                toast.success('Custom time applied');
                close();
            }
        };

        const isEffectivelyApproved = entry?.isApproved !== undefined 
            ? entry.isApproved 
            : (date < formatDateYYYYMMDD(new Date()));
        
        const isRejected = entry?.isRejected || false;

        return (
            <div className="w-80 p-4 space-y-4 bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-100 dark:border-slate-700">
                <div className="flex justify-between items-center pb-2 border-b dark:border-slate-700">
                    <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100">Update Status</h3>
                    <button onClick={close} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <Icons.X className="w-5 h-5" />
                    </button>
                </div>

                {shiftInfo && (
                    <div className="space-y-2">
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider">Final Status</label>
                        <div className="flex p-1 bg-gray-100 dark:bg-slate-900 rounded-xl">
                            <button
                                onClick={() => {
                                    if (onUpdate && entry?.shiftId) {
                                        onUpdate(staffId, date, entry.shiftId, 'ASSIGNED', false, entry?.departmentId, {
                                            isApproved: true,
                                            isRejected: false
                                        });
                                        toast.success('Shift approved');
                                    }
                                }}
                                className={`flex-1 flex items-center justify-center gap-2 py-1.5 text-[10px] font-bold rounded-lg transition-all ${
                                    isEffectivelyApproved && !isRejected
                                        ? 'bg-white dark:bg-slate-800 text-indigo-600 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
                                }`}
                            >
                                <Icons.CheckCircle className={`w-3.5 h-3.5 ${isEffectivelyApproved && !isRejected ? 'text-indigo-600' : 'text-gray-400'}`} />
                                Approved
                            </button>
                            <button
                                onClick={() => {
                                    if (onUpdate && entry?.shiftId) {
                                        onUpdate(staffId, date, entry.shiftId, 'ASSIGNED', false, entry?.departmentId, {
                                            isRejected: true,
                                            isApproved: false
                                        });
                                        toast.success('Shift rejected');
                                    }
                                }}
                                className={`flex-1 flex items-center justify-center gap-2 py-1.5 text-[10px] font-bold rounded-lg transition-all ${
                                    isRejected
                                        ? 'bg-white dark:bg-slate-800 text-rose-600 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
                                }`}
                            >
                                <Icons.XCircle className={`w-3.5 h-3.5 ${isRejected ? 'text-rose-600' : 'text-gray-400'}`} />
                                Rejected
                            </button>
                        </div>
                    </div>
                )}

                <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                        <Icons.Clock className="w-3 h-3" /> Available Shifts
                    </div>
                    <div className="grid grid-cols-1 gap-1 max-h-32 overflow-y-auto pr-1 custom-scrollbar">
                        {availableShifts.map(s => {
                            const isSelected = entry?.shiftId === s.id;
                            return (
                                <button 
                                    key={s.id} 
                                    onClick={() => { handleAssignmentAttempt(s.id, 'ASSIGNED'); close(); }}
                                    className={`flex items-center justify-between p-2 rounded-lg text-xs transition-all ${
                                        isSelected 
                                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 ring-1 ring-blue-200 dark:ring-blue-800' 
                                        : 'bg-gray-50 dark:bg-slate-900/50 hover:bg-gray-100 dark:hover:bg-slate-900'
                                    }`}
                                >
                                    <span className="font-semibold">{s.name}</span>
                                    <span className="text-[10px] opacity-70">{s.startTime}-{s.endTime}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {shiftInfo && (
                    <div className="p-2 bg-blue-50/50 dark:bg-blue-900/10 rounded-lg border border-blue-100 dark:border-blue-900/20 space-y-2">
                        <div className="flex items-center gap-1.5 text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider">
                            <Icons.Edit className="w-3 h-3" /> Custom Shift Time
                        </div>
                        <div className="flex items-center gap-2">
                            <input 
                                type="time" 
                                value={customStart} 
                                onChange={e => setCustomStart(e.target.value)}
                                className="flex-1 px-1.5 py-1 text-xs bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded outline-none focus:ring-1 focus:ring-blue-500 text-gray-900 dark:text-gray-100"
                            />
                            <span className="text-gray-400 text-xs">-</span>
                            <input 
                                type="time" 
                                value={customEnd} 
                                onChange={e => setCustomEnd(e.target.value)}
                                className="flex-1 px-1.5 py-1 text-xs bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded outline-none focus:ring-1 focus:ring-blue-500 text-gray-900 dark:text-gray-100"
                            />
                            <button 
                                onClick={handleApplyCustom}
                                className="p-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors shadow-sm"
                                title="Apply Custom Time"
                            >
                                <Icons.Check className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                )}

                <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                        <Icons.User className="w-3 h-3" /> Status & Leave
                    </div>
                    <div className="grid grid-cols-4 gap-1">
                        {statusLeaveOptions.map(status => (
                            <button 
                                key={status} 
                                onClick={() => { handleAssignmentAttempt(null, status); close(); }}
                                className="flex flex-col items-center gap-0.5 p-1 rounded-lg bg-gray-50 dark:bg-slate-900/50 hover:bg-gray-100 dark:hover:bg-slate-900 transition-all group"
                            >
                                <div className={`p-1 rounded-md ${statusConfig[status].color.split(' ')[0]}`}>
                                    {React.cloneElement(statusConfig[status].icon as React.ReactElement<{ className?: string }>, { className: 'w-3 h-3' })}
                                </div>
                                <span className="text-[8px] font-bold text-gray-700 dark:text-gray-300 leading-none truncate w-full text-center">{statusConfig[status].label}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {!readOnly && entry && entry.status !== 'UNASSIGNED' && (
                    <button 
                        onClick={() => { 
                            if (isShiftPastOrOngoing) {
                                setIsDeleteConfirmOpen(true);
                            } else {
                                handleAssignmentAttempt(null, 'UNASSIGNED'); 
                                close(); 
                            }
                        }}
                        className="w-full flex items-center justify-center gap-2 py-2 text-[10px] font-bold text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/10 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors border border-red-100 dark:border-red-900/30"
                    >
                        <Icons.Trash2 className="w-3 h-3" /> Delete Assignment
                    </button>
                )}
            </div>
        );
    };

    const AttendanceRequestPopoverContent = ({ close }: { close: () => void }) => {
        const [requestedStart, setRequestedStart] = useState(activeRequest?.requestedStartTime || shiftInfo?.startTime || '');
        const [requestedEnd, setRequestedEnd] = useState(activeRequest?.requestedEndTime || shiftInfo?.endTime || '');
        const [staffNote, setStaffNote] = useState(activeRequest?.staffNote || '');

        const handleSave = () => {
            if (onCreateAttendanceRequest) {
                onCreateAttendanceRequest(staffId, date, { 
                    requestedStartTime: requestedStart, 
                    requestedEndTime: requestedEnd,
                    staffNote
                });
            }
            close();
        };

        // Admin status controls (Approved/Rejected) should only be visible for pending requests if user is admin
        // or just let the main Attendance Module handle it.
        // For simplicity and following prompt, we focus on user creation here.

        return (
            <div className="p-5 w-[420px] space-y-5 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-slate-700">
                <div className="flex items-center justify-between border-b dark:border-slate-700 pb-3">
                    <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400">
                        <Icons.Clock className="w-5 h-5" />
                        <span className="text-xs font-bold uppercase tracking-wider">Attendance Change Request</span>
                    </div>
                    <button onClick={close} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <Icons.X className="w-5 h-5" />
                    </button>
                </div>

                <div className="space-y-4">
                    <div className="bg-gray-50 dark:bg-slate-900/50 p-3 rounded-xl border border-gray-100 dark:border-slate-700">
                        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-2 tracking-wider">Original Schedule</label>
                        <div className="flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300">
                            <span>{shiftInfo?.name || 'Assigned'}</span>
                            <span>{shiftInfo?.startTime} - {shiftInfo?.endTime}</span>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1.5 tracking-wider">Requested Start</label>
                            <input 
                                type="time" 
                                value={requestedStart} 
                                onChange={(e) => setRequestedStart(e.target.value)}
                                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 transition-all text-gray-900 dark:text-gray-100"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1.5 tracking-wider">Requested End</label>
                            <input 
                                type="time" 
                                value={requestedEnd} 
                                onChange={(e) => setRequestedEnd(e.target.value)}
                                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 transition-all text-gray-900 dark:text-gray-100"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1.5 tracking-wider">Note / Reason</label>
                        <textarea 
                            value={staffNote} 
                            onChange={(e) => setStaffNote(e.target.value)}
                            placeholder="Why are you changing these times?"
                            className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 min-h-[80px] resize-none transition-all text-gray-900 dark:text-gray-100"
                        />
                    </div>
                </div>

                <div className="flex gap-3 pt-2 border-t dark:border-slate-700">
                    <button 
                        onClick={handleSave}
                        disabled={hasPendingAttendanceRequest}
                        className={`flex-1 py-3 text-white text-sm font-bold rounded-xl transition-all shadow-md active:scale-[0.98] ${
                            hasPendingAttendanceRequest 
                            ? 'bg-gray-400 cursor-not-allowed opacity-50' 
                            : 'bg-indigo-600 hover:bg-indigo-700'
                        }`}
                    >
                        {hasPendingAttendanceRequest ? 'Request Pending...' : 'Submit Time Change'}
                    </button>
                    <button 
                        onClick={close}
                        className="px-4 py-3 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 text-sm font-bold rounded-xl hover:bg-gray-200 dark:hover:bg-slate-600 transition-all"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        );
    };

    const renderCellContent = () => {
        const originalContent = (() => {
            if (shiftInfo) {
                // Priority for display: 
                // 1. Approved Attendance Request
                // 2. Custom edited time
                // 3. Original assigned time snapshot
                
                const displayStartTime = (activeRequest?.status === 'APPROVED') 
                    ? activeRequest.requestedStartTime 
                    : (entry?.customStartTime || shiftInfo.startTime);
                
                const displayEndTime = (activeRequest?.status === 'APPROVED') 
                    ? activeRequest.requestedEndTime 
                    : (entry?.customEndTime || shiftInfo.endTime);

                const formattedStartTime = formatTimeToAMPM(displayStartTime);
                const formattedEndTime = formatTimeToAMPM(displayEndTime);

                return (
                    <div 
                        className="p-1.5 rounded-md text-white font-semibold text-left h-full w-full flex flex-col justify-start items-start pb-1.5 relative overflow-hidden" 
                        style={{ backgroundColor: shiftInfo.color }}
                        title={tooltipText}
                    >
                        <div className="flex items-center gap-1 z-10 w-full">
                            <span className="text-sm leading-tight flex-grow">
                                {formattedStartTime && formattedEndTime ? `${formattedStartTime}-${formattedEndTime}` : (shiftInfo.name || 'Assigned')}
                            </span>
                            {hasPendingAttendanceRequest && (
                                <div className="flex items-center gap-0.5" title="Pending Approval">
                                    <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                                </div>
                            )}
                            {activeRequest?.status === 'APPROVED' && <Icons.CheckCircle className="w-3 h-3 opacity-80" />}
                        </div>
                        <span className="text-xs opacity-90 leading-tight z-10">
                            {displayStartTime && displayEndTime ? `${displayStartTime} - ${displayEndTime}` : ''}
                        </span>
                        {assignedDepartment && (
                            <div className="absolute top-0 right-0 px-1 py-0.5 text-[8px] uppercase font-black bg-white text-gray-900 rounded-bl-md shadow-sm z-20 border-b border-l border-gray-100">
                                {assignedDepartment.abbreviation || assignedDepartment.name.substring(0, 3)}
                            </div>
                        )}
                        {hasPendingAttendanceRequest && (
                            <div className="absolute bottom-0 right-0 left-0 bg-amber-400/90 text-[8px] text-amber-900 py-0.5 text-center font-bold">
                                PENDING CHANGE
                            </div>
                        )}
                    </div>
                );
            }
            if (entry && entry.status !== 'UNASSIGNED') {
                const config = statusConfig[entry.status] || statusConfig['UNASSIGNED'];
                return (
                    <div 
                        className={`w-full h-full rounded-md text-sm flex items-center justify-start px-2 gap-1.5 font-medium ${config.color}`}
                        title={tooltipText}
                    >
                         {config.icon}
                        <span>{config.label}</span>
                    </div>
                );
            }
            return (
                <div className="w-full h-full rounded-md bg-gray-50 dark:bg-slate-800/50"></div>
            );
        })();

        return (
            <div className="relative h-full w-full">
                <div className={isSpecificUnavailability ? 'opacity-40' : ''}>
                    {originalContent}
                </div>
                {isSpecificUnavailability && (
                    <div className="absolute inset-0 flex items-center justify-center" title="Specifically Unavailable">
                        <Icons.XCircle className="w-6 h-6 text-red-600 dark:text-red-500" />
                    </div>
                )}
                {entry?.isForced && (
                    <div className="absolute bottom-1 right-1" title="This shift was force assigned against default availability.">
                        <Icons.AlertTriangle className="w-3.5 h-3.5 text-yellow-600 dark:text-yellow-400 bg-white dark:bg-slate-700 rounded-full p-0.5 shadow" />
                    </div>
                )}
            </div>
        );
    };

    const cellWrapper = (
        <div 
            className={`h-full w-full rounded-md transition-all duration-150 ${!isSpecificUnavailability && !readOnly ? 'cursor-pointer hover:ring-2 hover:ring-indigo-500 hover:ring-offset-1 dark:hover:ring-offset-slate-800' : isSpecificUnavailability ? 'cursor-not-allowed' : 'cursor-default'}`}
            title={tooltipText}
        >
            {renderCellContent()}
        </div>
    );

    return (
        <td
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            title={tooltipText}
            className={`align-middle h-16 border-l border-gray-200 dark:border-slate-700 transition-colors duration-150 ${isDragOver && !isSpecificUnavailability ? 'bg-indigo-100 dark:bg-indigo-900/50' : ''}`}
        >
            <div className="p-1 h-full w-full">
                {isSpecificUnavailability || (readOnly && !isShiftPastOrOngoing) ? (
                    <div 
                        title={tooltipText}
                        className="h-full w-full"
                    >
                        {cellWrapper}
                    </div>
                ) : (
                    <div 
                        onDoubleClick={() => {
                            if (shiftInfo && isShiftPastOrOngoing) {
                                setIsTimeUpdateModalOpen(true);
                            } else if (!isShiftPastOrOngoing && !readOnly) {
                                // Potentially open admin popover for future shifts
                            }
                        }}
                        className="h-full w-full"
                    >
                        {isAdmin && !isShiftPastOrOngoing ? (
                            <Popover 
                                wrapperClassName="h-full w-full" 
                                content={<PopoverContent close={() => {}} />}
                            >
                                <div className="h-full w-full" title={tooltipText}>
                                    {cellWrapper}
                                </div>
                            </Popover>
                        ) : (
                            <div className="h-full w-full" title={tooltipText}>
                                {cellWrapper}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {forceAssignData && (
                <ConfirmationModal
                    title="Force Assignment"
                    message={`${personName} is not normally available on this day. Do you want to force assign this shift?`}
                    confirmButtonText="Force Assign"
                    onConfirm={handleConfirmForceAssign}
                    onCancel={handleCancelForceAssign}
                />
            )}

            {isDeleteConfirmOpen && (
                <ConfirmationModal
                    title="Delete Past Assignment"
                    message="This shift has already passed. Are you sure you want to delete this assignment? This action cannot be undone."
                    onConfirm={() => {
                        handleAssignmentAttempt(null, 'UNASSIGNED');
                        setIsDeleteConfirmOpen(false);
                    }}
                    onCancel={() => setIsDeleteConfirmOpen(false)}
                    confirmButtonText="Delete"
                />
            )}

            {pendingShiftId && (
                <Modal title="Select Department" onClose={() => setPendingShiftId(null)} maxWidthClass="sm:max-w-md">
                    <div className="p-4">
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                            {personName} belongs to multiple departments. Please select which department to assign this shift to.
                        </p>
                        <div className="grid grid-cols-1 gap-2">
                            {staffDepartments.map(dep => (
                                <button 
                                    key={dep.id} 
                                    onClick={() => { 
                                        handleAssignmentAttempt(pendingShiftId, 'ASSIGNED', dep.id); 
                                        setPendingShiftId(null); 
                                    }}
                                    className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center gap-3 transition-colors group"
                                >
                                    <span className="w-4 h-4 rounded-full shadow-sm" style={{ backgroundColor: dep.color }}></span>
                                    <span className="font-medium text-gray-900 dark:text-gray-100 group-hover:text-indigo-600 dark:group-hover:text-indigo-400">{dep.name}</span>
                                    <Icons.ChevronRight className="w-4 h-4 ml-auto text-gray-400 group-hover:text-indigo-500" />
                                </button>
                            ))}
                        </div>
                    </div>
                </Modal>
            )}
            {isTimeUpdateModalOpen && shiftInfo && (
                <Modal title="Update Shift Attendance" onClose={() => setIsTimeUpdateModalOpen(false)}>
                    <AttendanceRequestPopoverContent close={() => setIsTimeUpdateModalOpen(false)} />
                </Modal>
            )}

 
        </td>
    );
};

export default ScheduleCell;
